import { Link } from "@/components/common/Link";
import { ArrowUpRight, CircleCheck as CheckCircle2, TrendingUp, ShieldCheck } from 'lucide-react';
export default function ExpertiseInAction() {
  const caseStudies = [{
    id: "case-study-1",
    eyebrow: "HERITAGE BOUTIQUE RESORT — RAJASTHAN",
    h3: "Plugging Extranet Rate Leaks & Setting Dynamic Daily Pricing",
    narrative: "A 68-key heritage resort was leaking net margin to high OTA commissions, unmanaged wholesale feeds, and static mid-week rates. I audited their extranets, set minimum stay restrictions on peak dates, and adjusted room rates daily based on live pickup.",
    metrics: {
      value: "15–25%",
      label: "RevPAR Growth in 90 Days",
      sub: "More bookings through the direct engine"
    },
    executionSteps: [{
      title: "Daily Pricing & Compset Tracking",
      desc: "Replaced flat seasonal rack rates with daily pricing based on actual market demand and local compset pickup pace."
    }, {
      title: "OTA Parity & Rate Leak Repairs",
      desc: "Traced and plugged unauthorized wholesale rates across Agoda, MakeMyTrip, and Booking.com to protect direct margins."
    }, {
      title: "Direct Booking Conversion",
      desc: "Added direct booking incentives, streamlined room category descriptions, and matched OTA rates on the hotel website."
    }],
    quote: "I audited the extranets, set MLOS on peak weekends, renegotiated commission tiers, and tracked pickup daily so we stopped giving away high-demand dates at base rates.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/luxury_heritage_palace_resort.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/luxury_heritage_palace_resort.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/luxury_heritage_palace_resort.png-wvc-sizes",
      alt: "Luxury heritage resort architectural facade and illuminated courtyard"
    },
    linkText: "Explore our revenue management services",
    linkHref: "/ServicesPage"
  }, {
    id: "case-study-2",
    eyebrow: "5-STAR METROPOLITAN HOTEL — DELHI NCR",
    h3: "Channel Manager Sync & Corporate Segment Pricing",
    narrative: "A 180-room commercial hotel struggled with delayed inventory updates and mismatched prices across channels. I reconfigured the two-way PMS-channel manager mapping and set structured hurdle rates for corporate room blocks.",
    metrics: {
      value: "15–25%",
      label: "Net Revenue Improvement",
      sub: "Measured over a 6-month consulting engagement"
    },
    executionSteps: [{
      title: "PMS & Channel Manager Sync",
      desc: "Configured clean two-way mapping between the PMS and channel manager to stop rate lag across every connected channel."
    }, {
      title: "Corporate & MICE Hurdle Rates",
      desc: "Established minimum base rates for corporate group blocks during high-demand convention dates."
    }, {
      title: "OTA Channel Mix & Commission Control",
      desc: "Shifted corporate repeat bookers from expensive OTA channels to direct negotiated accounts while increasing overall ADR."
    }],
    quote: "When your channel manager and PMS update within seconds, you stop losing high-demand rooms to cheap third-party rates and protect your GOPPAR.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/modern_luxury_hotel_suite_interior.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/modern_luxury_hotel_suite_interior.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/modern_luxury_hotel_suite_interior.webp-wvc-sizes",
      alt: "Modern luxury hotel executive suite with architectural interior design"
    },
    linkText: "See how we configure hotel distribution",
    linkHref: "/ServicesPage"
  }];
  return <section data-section-id="3794" id="expertise-in-action" className="relative bg-background text-foreground border-y border-border py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary/15 border border-secondary/30 rounded-md text-xs font-mono tracking-widest uppercase text-foreground mb-4">
            <TrendingUp className="size-3.5 text-secondary" />
            <span>CASE STUDIES &amp; REAL RESULTS</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Real Hotels. Real Numbers.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Direct outcomes from independent properties where we fixed rate leaks, updated daily prices, and protected bottom-line profit. Review our{" "}
            <Link to="/CaseStudiesPage" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors">
              case studies and proven results
            </Link>{" "}
            across independent hotels.
          </p>
        </div>

        {/* Alternating Case Studies List */}
        <div className="space-y-20 lg:space-y-32">
          {caseStudies.map((study, idx) => {
          const isEven = idx % 2 === 1;
          return <div key={study.id} data-index={idx} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                {/* Visual Imagery Column with Floating Metric Badge */}
                <div className={`relative order-1 ${isEven ? "lg:col-span-7 lg:order-2" : "lg:col-span-7 lg:order-1"}`}>
                  <div className="relative aspect-[16/10] sm:aspect-[16/9] lg:aspect-[4/3] rounded-xl overflow-hidden border border-border shadow-lg bg-muted group">
                    <img src={study.image.src} data-wvc-srcset={study.image.srcset} data-wvc-sizes={study.image.sizes} alt={study.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />

                    {/* Floating Metric Badge */}
                    <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-primary/95 backdrop-blur-md text-primary-foreground p-4 sm:p-5 rounded-xl border border-secondary/40 shadow-xl max-w-[280px]">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl sm:text-3xl font-bold text-secondary tracking-tight">
                          {study.metrics.value}
                        </span>
                        <ShieldCheck className="size-4 text-secondary shrink-0" />
                      </div>
                      <p className="text-sm font-semibold text-primary-foreground mt-0.5">
                        {study.metrics.label}
                      </p>
                      <p className="text-xs text-primary-foreground/70 font-mono mt-1">
                        {study.metrics.sub}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Narrative Content Column */}
                <div className={`order-2 ${isEven ? "lg:col-span-5 lg:order-1" : "lg:col-span-5 lg:order-2"}`}>
                  <div className="space-y-6">
                    {/* Eyebrow */}
                    <div className="text-xs font-mono font-semibold tracking-wider text-muted-foreground uppercase">
                      {study.eyebrow}
                    </div>

                    {/* H3 Title */}
                    <h3 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight leading-tight">
                      {study.h3}
                    </h3>

                    {/* Narrative Description */}
                    <p className="text-base text-foreground/80 leading-relaxed">
                      {study.narrative}
                    </p>

                    {/* Execution Breakdown Steps */}
                    <div className="space-y-3.5 pt-2">
                      <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold">
                        EXECUTION MILESTONES
                      </p>
                      <div className="space-y-3">
                        {study.executionSteps.map((step, stepIdx) => <div key={stepIdx} data-index={stepIdx} className="flex items-start gap-3 text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-secondary shrink-0 mt-1" />
                            <div>
                              <span className="font-semibold text-foreground">
                                {step.title}:
                              </span>{" "}
                              <span className="text-muted-foreground">{step.desc}</span>
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Gold-Bordered Blockquote */}
                    <blockquote className="border-l-4 border-secondary bg-secondary/10 p-4 sm:p-5 rounded-lg italic text-foreground text-sm sm:text-base leading-relaxed my-6">
                      "{study.quote}"
                      <footer className="not-italic font-semibold text-xs tracking-wider uppercase text-foreground/80 block mt-2">
                        — <Link to="/AboutPage" className="hover:text-secondary transition-colors">Sunil Singh</Link>, Revenue Strategist
                      </footer>
                    </blockquote>

                    {/* Animated Underline Link */}
                    <div className="pt-2">
                      <Link to={study.linkHref} className="group/link inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-secondary-foreground transition-colors duration-200">
                        <span className="relative pb-0.5 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-secondary after:transition-all after:duration-300 group-hover/link:after:w-full">
                          {study.linkText}
                        </span>
                        <ArrowUpRight className="size-4 text-secondary transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}